/* rv-image-with-text.js — scroll-gates the band's fade-up (original code).
   The band's 0.8s fade-up runs at load by default. When the fade is enabled, the inline script inside the band
   pauses it before first paint (.rv-image-with-text__band--gated); this file marks each gated band as watched and
   swaps --gated for --in-view when the band enters the viewport. Fail-visible by design: if this file never runs,
   the inline script lifts the gate itself on window load / after 3 s, and with scripts off the animation plays at
   load. prefers-reduced-motion is handled in the CSS (animation: none).
   Fallback while a band is still gated: an IntersectionObserver is the primary trigger, and a passive scroll /
   resize listener plus a low-frequency repeating check reveal the band as soon as it is on screen even if the
   observer never fires for it (unsupported or throwing IO, a band inside a display:none ancestor at load that is
   shown later). All fallback hooks are removed once the last gated band is revealed.
   Theme editor: a re-rendered section replaces its DOM, so a still-gated old band would sit in the pending list forever
   (a detached node never reads as on screen) and keep the poll + listeners alive. Bands are therefore dropped on
   shopify:section:unload and, as a belt-and-braces check, whenever a sweep finds one no longer in the document. */
(function () {
  'use strict';

  var SEL_BAND = '.rv-image-with-text__band--fade';
  var CLASS_GATED = 'rv-image-with-text__band--gated';
  var CLASS_IN_VIEW = 'rv-image-with-text__band--in-view';
  var ATTR_WATCHED = 'data-rv-iwt-watched';
  var SAFETY_MS = 3000;
  var POLL_MS = 1000;

  var pending = [];
  var observer = null;
  var pollTimer = null;
  var hooked = false;

  function detached(el) {
    if (typeof el.isConnected === 'boolean') return !el.isConnected;
    return !document.documentElement.contains(el);
  }

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    var h = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > 0 && r.top < h && (r.width > 0 || r.height > 0);
  }

  function forget(el) {
    var i = pending.indexOf(el);
    if (i !== -1) pending.splice(i, 1);
    if (observer) observer.unobserve(el);
    if (!pending.length) unhook();
  }

  function reveal(el) {
    el.classList.remove(CLASS_GATED);
    el.classList.add(CLASS_IN_VIEW);
    forget(el);
  }

  /* Reveal every still-gated band that is on screen right now (scroll / resize / poll / safety timer). */
  function sweep() {
    var list = pending.slice();
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (detached(el) || el.classList.contains(CLASS_IN_VIEW) || !el.classList.contains(CLASS_GATED)) { forget(el); continue; }
      if (onScreen(el)) reveal(el);
    }
  }

  function hook() {
    if (hooked) return;
    hooked = true;
    window.addEventListener('scroll', sweep, { passive: true });
    window.addEventListener('resize', sweep, { passive: true });
    pollTimer = window.setInterval(sweep, POLL_MS);
  }

  function unhook() {
    if (!hooked) return;
    hooked = false;
    window.removeEventListener('scroll', sweep);
    window.removeEventListener('resize', sweep);
    if (pollTimer !== null) { window.clearInterval(pollTimer); pollTimer = null; }
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    try {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target);
        });
      }, { threshold: 0 });
    } catch (e) {
      observer = null;
    }
    return observer;
  }

  function init(root) {
    var bands = (root || document).querySelectorAll(SEL_BAND + ':not(.' + CLASS_IN_VIEW + ')');
    if (!bands.length) return;
    var io = getObserver();
    Array.prototype.forEach.call(bands, function (el) {
      el.setAttribute(ATTR_WATCHED, '');
      /* Ungated band (theme editor re-render, or the inline gate never ran): nothing to hold, nothing to watch. */
      if (!el.classList.contains(CLASS_GATED)) { el.classList.add(CLASS_IN_VIEW); return; }
      if (!io) { reveal(el); return; }
      if (pending.indexOf(el) === -1) pending.push(el);
      io.observe(el);
    });
    if (pending.length) {
      hook();
      /* Keeps the scroll gate: if the observer has not fired by now but a band is on screen, reveal it. */
      window.setTimeout(sweep, SAFETY_MS);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  /* Theme editor re-renders via innerHTML (inline scripts do not run there), so the band arrives ungated and
     this pass only marks it watched + in view. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  /* The editor fires unload for the outgoing section before it re-renders / removes it: drop its bands so the
     pending list (and with it the poll + scroll/resize listeners) does not outlive the DOM they watched. */
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target;
    if (!root || !root.querySelectorAll) return;
    var list = pending.slice();
    for (var i = 0; i < list.length; i++) {
      if (root === list[i] || root.contains(list[i])) forget(list[i]);
    }
  });
})();
